import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type ReactNode,
} from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Subscription, Plan, planLevel } from '../models/Subscription'
import { getSubscription } from '../services/api/subscription'
import { getMyOrg } from '../services/api/organisation'
import { queryKeys } from '../services/queryKeys'
import { useUserContext } from './UserContext'

interface SubscriptionContextType {
  subscription: Subscription | null
  loading: boolean
  refresh: () => Promise<void>
  isPro: boolean
  isCaregiver: boolean
  isFree: boolean
  isTrial: boolean
  isEnterprise: boolean
  organisationName: string | null
  trialDaysLeft: number | null
  hasMinimumPlan: (minimum: Plan) => boolean
}

export const SubscriptionContext = createContext<
  SubscriptionContextType | undefined
>(undefined)

export const useSubscription = (): SubscriptionContextType => {
  const context = useContext(SubscriptionContext)

  if (!context) {
    throw new Error('useSubscription must be used within SubscriptionProvider')
  }

  return context
}

interface SubscriptionProviderProps {
  children: ReactNode
}

function SubscriptionProvider({ children }: SubscriptionProviderProps) {
  const { user } = useUserContext()
  const queryClient = useQueryClient()

  const subscriptionQuery = useQuery({
    queryKey: queryKeys.subscription(user?.userId),
    enabled: Boolean(user?.userId),
    queryFn: async (): Promise<Subscription> => {
      const [sub, org] = await Promise.all([
        getSubscription(),
        getMyOrg().catch(() => null),
      ])

      if (org) {
        sub.plan = 'enterprise'
        sub.status = 'active'
        sub.organisationId = org.organisationId
        sub.organisationName = org.organisationName
        sub.organisationSlug = org.organisationSlug
      }

      return sub
    },
  })

  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: queryKeys.subscription(user?.userId),
    })
  }, [queryClient, user?.userId])

  const subscription =
    subscriptionQuery.data ??
    (user?.userId ? null : { plan: 'free', status: 'active' as const })
  const loading =
    Boolean(user?.userId) && (subscriptionQuery.isPending || subscriptionQuery.isFetching)
  const currentPlan = subscription?.plan
  const isActive =
    subscription?.status === 'active' || subscription?.status === 'trialing'
  const isTrial = subscription?.status === 'trialing'

  const trialDaysLeft = (() => {
    if (!isTrial || !subscription?.trialEndsAt) return null

    const now = new Date()
    const end = new Date(subscription.trialEndsAt)
    const diffMs = end.getTime() - now.getTime()
    return Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)))
  })()

  const isPro =
    currentPlan != null && isActive && planLevel(currentPlan) >= planLevel('pro')
  const isCaregiver = isActive && currentPlan === 'caregiver'
  const isFree = currentPlan === 'free' || (!subscription && !loading && !subscriptionQuery.isError)
  const isEnterprise = isActive && currentPlan === 'enterprise'
  const hasMinimumPlan = useCallback(
    (minimum: Plan) =>
      Boolean(currentPlan) &&
      isActive &&
      planLevel(currentPlan as Plan) >= planLevel(minimum),
    [currentPlan, isActive]
  )

  const value = useMemo<SubscriptionContextType>(
    () => ({
      subscription,
      loading,
      refresh,
      isPro,
      isCaregiver,
      isFree,
      isTrial,
      isEnterprise,
      organisationName: subscription?.organisationName ?? null,
      trialDaysLeft,
      hasMinimumPlan,
    }),
    [
      currentPlan,
      hasMinimumPlan,
      isActive,
      isCaregiver,
      isEnterprise,
      isFree,
      isPro,
      isTrial,
      loading,
      refresh,
      subscription,
      trialDaysLeft,
    ]
  )

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  )
}

export default SubscriptionProvider
